/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  FlatList,
  RefreshControl,
  SectionList,
  TextInput,
  Modal,
  Animated,
  Dimensions,
  ActivityIndicator,
  Alert,
  Platform,
  StatusBar,
  Share,
  Linking,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  Feather,
  AntDesign,
} from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// ════════════════════════════════════════════════════════════════
// API CONFIG
// ════════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// ════════════════════════════════════════════════════════════════
// CONSTANTS & THEME
// ════════════════════════════════════════════════════════════════
const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const CARD_WIDTH = SCREEN_WIDTH * 0.75;

const THEME = {
  primary: '#0e4e96',
  primaryLight: '#1a6bc4',
  primaryDark: '#0a3a6e',
  primaryBg: '#f0f7ff',
  secondary: '#10b981',
  secondaryLight: '#d1fae5',
  accent: '#f59e0b',
  accentLight: '#fef3c7',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  bg: '#f5f7fa',
  card: '#ffffff',
  text: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  shadow: '#000',
  white: '#ffffff',
  gold: '#FFC107',
  gradient1: '#0e4e96',
  gradient2: '#1a6bc4',
};

const SHADOW = {
  shadowColor: THEME.shadow,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.08,
  shadowRadius: 8,
  elevation: 3,
};

const SHADOW_HEAVY = {
  shadowColor: THEME.shadow,
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.12,
  shadowRadius: 12,
  elevation: 6,
};

// ════════════════════════════════════════════════════════════════
// JOBENG LOGO
// ════════════════════════════════════════════════════════════════
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

// ════════════════════════════════════════════════════════════════
// MOCK DATA (Comprehensive)
// ════════════════════════════════════════════════════════════════
const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const SORT_OPTIONS = ['Recommended', 'Most Popular', 'Newest', 'Highest Rated', 'Shortest Duration'];
const FILTER_DURATIONS = ['All', '< 2 weeks', '2-4 weeks', '4-8 weeks', '8+ weeks'];

const mockData = {
  // ── User Stats ──
  userStats: {
    coursesCompleted: 12,
    hoursLearned: 86,
    certificatesEarned: 5,
    currentStreak: 7,
    longestStreak: 21,
    skillPoints: 2450,
    rank: 'Gold Learner',
    weeklyGoal: 5, // hours
    weeklyProgress: 3.5,
    completionRate: 87,
    averageScore: 92,
  },

  // ── Weekly Activity (last 7 days) ──
  weeklyActivity: [
    { day: 'Mon', hours: 1.5 },
    { day: 'Tue', hours: 0.5 },
    { day: 'Wed', hours: 2.0 },
    { day: 'Thu', hours: 0 },
    { day: 'Fri', hours: 1.0 },
    { day: 'Sat', hours: 0 },
    { day: 'Sun', hours: 0.5 },
  ],

  // ── Skill Radar ──
  skills: [
    { name: 'Leadership', level: 78, target: 90 },
    { name: 'Technical', level: 85, target: 95 },
    { name: 'Communication', level: 65, target: 80 },
    { name: 'Management', level: 72, target: 85 },
    { name: 'Analytics', level: 58, target: 75 },
    { name: 'Safety', level: 90, target: 95 },
  ],

  // ── Featured Courses ──
  featuredCourses: [
    {
      id: '1',
      title: 'Project Management Essentials',
      instructor: 'Sarah Johnson',
      instructorRole: 'PMP Certified Trainer',
      duration: '8 weeks',
      durationHours: 40,
      level: 'Beginner',
      rating: 4.8,
      reviewCount: 342,
      enrolledCount: 1280,
      tags: ['Management', 'Leadership', 'Agile'],
      image: require('../../../assets/jobeng_icon.png'),
      price: 'Free',
      language: 'English',
      hasSubtitles: true,
      lastUpdated: 'Mar 2026',
      modules: 12,
      assignments: 8,
      certificate: true,
      description: 'Master the fundamentals of project management including planning, execution, monitoring, and closing projects successfully.',
    },
    {
      id: '2',
      title: 'Advanced Data Analysis with Python',
      instructor: 'Michael Chen',
      instructorRole: 'Sr. Data Scientist at TechCorp',
      duration: '6 weeks',
      durationHours: 30,
      level: 'Advanced',
      rating: 4.9,
      reviewCount: 218,
      enrolledCount: 890,
      tags: ['Data', 'Analytics', 'Python'],
      image: require('../../../assets/jobeng_icon.png'),
      price: 'Premium',
      language: 'English',
      hasSubtitles: true,
      lastUpdated: 'Feb 2026',
      modules: 10,
      assignments: 15,
      certificate: true,
      description: 'Deep dive into data analysis using Python, pandas, numpy, and visualization libraries for business insights.',
    },
    {
      id: '3',
      title: 'Digital Marketing Mastery',
      instructor: 'Emma Williams',
      instructorRole: 'CMO at GrowthHub',
      duration: '10 weeks',
      durationHours: 50,
      level: 'Intermediate',
      rating: 4.7,
      reviewCount: 156,
      enrolledCount: 2100,
      tags: ['Marketing', 'Digital', 'SEO', 'Social Media'],
      image: require('../../../assets/jobeng_icon.png'),
      price: 'Free',
      language: 'English',
      hasSubtitles: true,
      lastUpdated: 'Jan 2026',
      modules: 14,
      assignments: 10,
      certificate: true,
      description: 'Comprehensive guide to digital marketing strategies including SEO, SEM, social media, and content marketing.',
    },
    {
      id: '4',
      title: 'Construction Safety & Compliance',
      instructor: 'Robert Al-Farsi',
      instructorRole: 'HSE Director',
      duration: '4 weeks',
      durationHours: 20,
      level: 'Beginner',
      rating: 4.6,
      reviewCount: 98,
      enrolledCount: 560,
      tags: ['Safety', 'Compliance', 'Construction'],
      image: require('../../../assets/jobeng_icon.png'),
      price: 'Free',
      language: 'English',
      hasSubtitles: true,
      lastUpdated: 'Apr 2026',
      modules: 8,
      assignments: 4,
      certificate: true,
      description: 'Essential safety training for construction professionals covering OSHA standards and best practices.',
    },
  ],

  // ── Categories ──
  categories: [
    { id: '1', name: 'Business', icon: 'briefcase-outline', courses: 24, color: '#3b82f6', bgColor: '#eff6ff' },
    { id: '2', name: 'Technology', icon: 'laptop', courses: 38, color: '#8b5cf6', bgColor: '#f5f3ff' },
    { id: '3', name: 'Design', icon: 'palette-outline', courses: 15, color: '#ec4899', bgColor: '#fdf2f8' },
    { id: '4', name: 'Leadership', icon: 'account-group', courses: 12, color: '#f59e0b', bgColor: '#fffbeb' },
    { id: '5', name: 'Finance', icon: 'cash-multiple', courses: 18, color: '#10b981', bgColor: '#ecfdf5' },
    { id: '6', name: 'Health & Safety', icon: 'shield-check-outline', courses: 9, color: '#ef4444', bgColor: '#fef2f2' },
    { id: '7', name: 'Engineering', icon: 'cog-outline', courses: 21, color: '#6366f1', bgColor: '#eef2ff' },
    { id: '8', name: 'Languages', icon: 'translate', courses: 14, color: '#14b8a6', bgColor: '#f0fdfa' },
  ],

  // ── Learning Paths ──
  learningPaths: [
    {
      id: 'lp1',
      title: 'Full Stack Project Manager',
      description: 'Go from beginner to certified PM in 6 months',
      coursesCount: 6,
      totalHours: 120,
      enrolledCount: 450,
      completedCount: 0,
      level: 'Beginner → Advanced',
      icon: 'road-variant',
      color: '#3b82f6',
      courses: ['Project Management Essentials', 'Agile Methodology', 'Risk Management', 'Stakeholder Communication', 'Advanced PM Tools', 'PM Capstone Project'],
    },
    {
      id: 'lp2',
      title: 'Data Analytics Professional',
      description: 'Master data-driven decision making',
      coursesCount: 5,
      totalHours: 100,
      enrolledCount: 320,
      completedCount: 1,
      level: 'Intermediate → Expert',
      icon: 'chart-line',
      color: '#8b5cf6',
      courses: ['Statistics Fundamentals', 'SQL for Analysts', 'Python Data Analysis', 'Data Visualization', 'Machine Learning Basics'],
    },
    {
      id: 'lp3',
      title: 'HSE Specialist Certification',
      description: 'Complete health, safety & environment program',
      coursesCount: 4,
      totalHours: 60,
      enrolledCount: 280,
      completedCount: 2,
      level: 'Beginner → Intermediate',
      icon: 'shield-star',
      color: '#10b981',
      courses: ['Workplace Safety Fundamentals', 'Risk Assessment', 'Environmental Compliance', 'Emergency Response'],
    },
  ],

  // ── Recommended Courses ──
  recommendedCourses: [
    { id: '10', title: 'Effective Communication Skills', level: 'Beginner', duration: '4 weeks', durationHours: 16, matchScore: 95, rating: 4.7, enrolledCount: 980, tags: ['Soft Skills'], instructor: 'Dr. Amira Khalil' },
    { id: '11', title: 'Agile Methodology & Scrum', level: 'Intermediate', duration: '6 weeks', durationHours: 24, matchScore: 88, rating: 4.8, enrolledCount: 1120, tags: ['Agile', 'Scrum'], instructor: 'James Porter' },
    { id: '12', title: 'Construction Safety Standards', level: 'Beginner', duration: '3 weeks', durationHours: 12, matchScore: 82, rating: 4.5, enrolledCount: 670, tags: ['Safety'], instructor: 'Khalid Mansour' },
    { id: '13', title: 'Financial Modelling in Excel', level: 'Intermediate', duration: '5 weeks', durationHours: 20, matchScore: 79, rating: 4.6, enrolledCount: 540, tags: ['Finance', 'Excel'], instructor: 'Rachel Kim' },
    { id: '14', title: 'Team Leadership Essentials', level: 'Beginner', duration: '4 weeks', durationHours: 16, matchScore: 76, rating: 4.4, enrolledCount: 820, tags: ['Leadership'], instructor: 'Omar Hassan' },
  ],

  // ── In Progress ──
  inProgress: [
    {
      id: 'ip1',
      title: 'Workplace Safety Fundamentals',
      progress: 72,
      nextLesson: 'Module 8: Emergency Procedures',
      dueDate: '30 Apr 2026',
      totalModules: 10,
      completedModules: 7,
      lastAccessedDate: '10 Apr 2026',
      timeSpent: '14h 30m',
      estimatedTimeLeft: '5h 45m',
      instructor: 'Khalid Mansour',
      certificate: true,
    },
    {
      id: 'ip2',
      title: 'Advanced Excel for Business',
      progress: 45,
      nextLesson: 'Module 5: Pivot Tables Masterclass',
      dueDate: '15 May 2026',
      totalModules: 12,
      completedModules: 5,
      lastAccessedDate: '8 Apr 2026',
      timeSpent: '9h 15m',
      estimatedTimeLeft: '11h 20m',
      instructor: 'Rachel Kim',
      certificate: true,
    },
    {
      id: 'ip3',
      title: 'Leadership Communication',
      progress: 20,
      nextLesson: 'Module 3: Active Listening',
      dueDate: '1 Jun 2026',
      totalModules: 8,
      completedModules: 1,
      lastAccessedDate: '5 Apr 2026',
      timeSpent: '3h 40m',
      estimatedTimeLeft: '14h 50m',
      instructor: 'Dr. Amira Khalil',
      certificate: true,
    },
  ],

  // ── Completed ──
  completedCourses: [
    { id: 'c1', title: 'Introduction to Project Management', completedDate: '15 Mar 2026', score: 94, certificate: true, certificateId: 'CERT-PM-001', hoursLogged: 38 },
    { id: 'c2', title: 'Excel Basics for Professionals', completedDate: '1 Feb 2026', score: 88, certificate: true, certificateId: 'CERT-EX-002', hoursLogged: 16 },
    { id: 'c3', title: 'Workplace Ethics & Compliance', completedDate: '20 Dec 2025', score: 96, certificate: true, certificateId: 'CERT-EC-003', hoursLogged: 10 },
    { id: 'c4', title: 'Time Management Mastery', completedDate: '5 Nov 2025', score: 91, certificate: false, certificateId: null, hoursLogged: 12 },
    { id: 'c5', title: 'Team Collaboration Tools', completedDate: '10 Oct 2025', score: 85, certificate: true, certificateId: 'CERT-TC-004', hoursLogged: 8 },
  ],

  // ── Upcoming Webinars ──
  upcomingWebinars: [
    { id: '20', title: 'Future of Remote Work', presenter: 'Dr. Alex Morgan', presenterRole: 'Workplace Futurist', date: '25 Apr 2026', time: '14:00 GMT', registeredCount: 234, duration: '90 min', isFree: true, isLive: false, topics: ['Remote Work', 'Productivity', 'Culture'] },
    { id: '21', title: 'AI in Recruitment & HR', presenter: 'Lisa Park', presenterRole: 'HR Tech Lead', date: '2 May 2026', time: '10:00 GMT', registeredCount: 189, duration: '60 min', isFree: true, isLive: false, topics: ['AI', 'Recruitment', 'HR Tech'] },
    { id: '22', title: 'Building High-Performance Teams', presenter: 'Carlos Rivera', presenterRole: 'VP of People', date: '10 May 2026', time: '16:00 GMT', registeredCount: 312, duration: '75 min', isFree: false, isLive: false, topics: ['Teams', 'Leadership', 'Culture'] },
  ],

  // ── Certificates ──
  certificates: [
    { id: 'cert1', title: 'Project Management Fundamentals', issuedDate: '15 Mar 2026', certificateId: 'CERT-PM-001', issuer: 'JOBENG Academy', expiryDate: '15 Mar 2028', status: 'Active', verificationUrl: 'https://verify.jobeng.ai/CERT-PM-001' },
    { id: 'cert2', title: 'Excel Professional', issuedDate: '1 Feb 2026', certificateId: 'CERT-EX-002', issuer: 'JOBENG Academy', expiryDate: null, status: 'Active', verificationUrl: 'https://verify.jobeng.ai/CERT-EX-002' },
    { id: 'cert3', title: 'Ethics & Compliance', issuedDate: '20 Dec 2025', certificateId: 'CERT-EC-003', issuer: 'JOBENG Academy', expiryDate: '20 Dec 2027', status: 'Active', verificationUrl: 'https://verify.jobeng.ai/CERT-EC-003' },
    { id: 'cert4', title: 'Team Collaboration', issuedDate: '10 Oct 2025', certificateId: 'CERT-TC-004', issuer: 'JOBENG Academy', expiryDate: null, status: 'Active', verificationUrl: 'https://verify.jobeng.ai/CERT-TC-004' },
    { id: 'cert5', title: 'Safety Awareness 2024', issuedDate: '5 Jun 2024', certificateId: 'CERT-SA-005', issuer: 'JOBENG Academy', expiryDate: '5 Jun 2025', status: 'Expired', verificationUrl: 'https://verify.jobeng.ai/CERT-SA-005' },
  ],

  // ── Notifications ──
  notifications: [
    { id: 'n1', type: 'deadline', message: 'Workplace Safety due in 20 days', timestamp: '2 hours ago', read: false, courseId: 'ip1' },
    { id: 'n2', type: 'achievement', message: 'You earned the "7 Day Streak" badge!', timestamp: '1 day ago', read: false },
    { id: 'n3', type: 'webinar', message: 'Webinar "Future of Remote Work" starts in 13 days', timestamp: '2 days ago', read: true, webinarId: '20' },
    { id: 'n4', type: 'new_course', message: 'New course: "AI for Business Leaders" just launched', timestamp: '3 days ago', read: true },
    { id: 'n5', type: 'certificate', message: 'Your certificate for "Safety Awareness" expires soon', timestamp: '5 days ago', read: true, certId: 'cert5' },
  ],

  // ── Trending Topics ──
  trendingTopics: [
    { id: 't1', name: 'Artificial Intelligence', growth: '+45%' },
    { id: 't2', name: 'Project Management', growth: '+32%' },
    { id: 't3', name: 'Cybersecurity', growth: '+28%' },
    { id: 't4', name: 'Data Analytics', growth: '+25%' },
    { id: 't5', name: 'Leadership', growth: '+22%' },
  ],

  // ── Leaderboard ──
  leaderboard: [
    { rank: 1, name: 'Ahmad Al-Rashid', points: 3200, avatar: null, department: 'Engineering' },
    { rank: 2, name: 'Fatima Zahra', points: 2980, avatar: null, department: 'Operations' },
    { rank: 3, name: 'You', points: 2450, avatar: null, department: 'Management', isCurrentUser: true },
    { rank: 4, name: 'David Park', points: 2310, avatar: null, department: 'HR' },
    { rank: 5, name: 'Maria Santos', points: 2150, avatar: null, department: 'Finance' },
  ],
};

// ════════════════════════════════════════════════════════════════
// REUSABLE SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════

// ── Animated Progress Bar ──
const AnimatedProgressBar = ({ progress, color = THEME.primary, height = 6, showLabel = false }) => {
  const animValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(animValue, {
      toValue: progress,
      duration: 800,
      useNativeDriver: false,
    }).start();
  }, [progress]);

  const widthInterpolation = animValue.interpolate({
    inputRange: [0, 100],
    outputRange: ['0%', '100%'],
    extrapolate: 'clamp',
  });

  return (
    <View style={{ width: '100%' }}>
      <View style={[styles.progressBarContainer, { height }]}>
        <Animated.View
          style={[
            styles.progressBar,
            {
              width: widthInterpolation,
              backgroundColor: color,
              height,
            },
          ]}
        />
      </View>
      {showLabel && (
        <Text style={styles.progressLabelText}>{Math.round(progress)}% complete</Text>
      )}
    </View>
  );
};

// ── Star Rating ──
const StarRating = ({ rating, size = 14, showCount = false, count = 0 }) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

  return (
    <View style={styles.starContainer}>
      {[...Array(fullStars)].map((_, i) => (
        <MaterialCommunityIcons key={`f${i}`} name="star" size={size} color={THEME.gold} />
      ))}
      {halfStar && <MaterialCommunityIcons name="star-half-full" size={size} color={THEME.gold} />}
      {[...Array(emptyStars)].map((_, i) => (
        <MaterialCommunityIcons key={`e${i}`} name="star-outline" size={size} color={THEME.gold} />
      ))}
      <Text style={[styles.ratingText, { fontSize: size - 1 }]}>{rating}</Text>
      {showCount && <Text style={[styles.ratingCount, { fontSize: size - 2 }]}>({count})</Text>}
    </View>
  );
};

// ── Badge / Chip ──
const Chip = ({ label, color = THEME.primary, bgColor = THEME.primaryBg, icon, small = false }) => (
  <View style={[styles.chip, { backgroundColor: bgColor }, small && styles.chipSmall]}>
    {icon && <MaterialCommunityIcons name={icon} size={small ? 10 : 12} color={color} style={{ marginRight: 3 }} />}
    <Text style={[styles.chipText, { color }, small && styles.chipTextSmall]}>{label}</Text>
  </View>
);

// ── Level Indicator ──
const LevelBadge = ({ level }) => {
  const config = {
    Beginner: { color: '#10b981', bg: '#ecfdf5', icon: 'signal-cellular-1' },
    Intermediate: { color: '#f59e0b', bg: '#fffbeb', icon: 'signal-cellular-2' },
    Advanced: { color: '#ef4444', bg: '#fef2f2', icon: 'signal-cellular-3' },
    Expert: { color: '#8b5cf6', bg: '#f5f3ff', icon: 'signal-cellular-outline' },
  };
  const c = config[level] || config.Beginner;
  return <Chip label={level} color={c.color} bgColor={c.bg} small />;
};

// ── Empty State ──
const EmptyState = ({ icon, title, subtitle, actionLabel, onAction }) => (
  <View style={styles.emptyState}>
    <MaterialCommunityIcons name={icon || 'folder-open-outline'} size={56} color={THEME.textMuted} />
    <Text style={styles.emptyStateText}>{title}</Text>
    <Text style={styles.emptyStateSubtext}>{subtitle}</Text>
    {actionLabel && (
      <TouchableOpacity style={styles.emptyStateButton} onPress={onAction}>
        <Text style={styles.emptyStateButtonText}>{actionLabel}</Text>
      </TouchableOpacity>
    )}
  </View>
);

// ── Section Header ──
const SectionHeader = ({ title, actionLabel, onAction, icon }) => (
  <View style={styles.sectionHeaderRow}>
    <View style={styles.sectionHeaderLeft}>
      {icon && <MaterialCommunityIcons name={icon} size={20} color={THEME.primary} style={{ marginRight: 6 }} />}
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
    {actionLabel && (
      <TouchableOpacity style={styles.sectionAction} onPress={onAction}>
        <Text style={styles.sectionActionText}>{actionLabel}</Text>
        <Ionicons name="chevron-forward" size={14} color={THEME.primary} />
      </TouchableOpacity>
    )}
  </View>
);

// ── Notification Badge ──
const NotificationBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <View style={styles.notificationBadge}>
      <Text style={styles.notificationBadgeText}>{count > 9 ? '9+' : count}</Text>
    </View>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN SCREEN COMPONENT
// ════════════════════════════════════════════════════════════════
const DevelopmentTrainingScreen = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // ── State ──
  const [activeTab, setActiveTab] = useState('Explore');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedSort, setSelectedSort] = useState('Recommended');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [bookmarkedCourses, setBookmarkedCourses] = useState([]);
  const [registeredWebinars, setRegisteredWebinars] = useState([]);
  const [expandedPaths, setExpandedPaths] = useState([]);
  const [myLearningSubTab, setMyLearningSubTab] = useState('In Progress');
  const [showCourseDetail, setShowCourseDetail] = useState(null);
  const [notifications, setNotifications] = useState(mockData.notifications);
  const [showCategories, setShowCategories] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('All');
  const [showCertificateModal, setShowCertificateModal] = useState(null);

  // ── Refs ──
  const scrollRef = useRef(null);
  const searchInputRef = useRef(null);
  const headerAnim = useRef(new Animated.Value(0)).current;

  // ── Tabs ──
  const TABS = ['Explore', 'My Learning', 'Paths', 'Certificates'];

  // ── Initialize from route params ──
  useEffect(() => {
    if (route?.params?.initialTab) {
      const tabMap = {
        Courses: 'Explore',
        'My Learning': 'My Learning',
        Paths: 'Paths',
        Certificates: 'Certificates',
      };
      const mapped = tabMap[route.params.initialTab] || route.params.initialTab;
      if (TABS.includes(mapped)) {
        setActiveTab(mapped);
      }
    }
  }, [route?.params]);

  // ── Computed Values ──
  const unreadNotifications = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const filteredCourses = useMemo(() => {
    let courses = [...mockData.recommendedCourses];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      courses = courses.filter(
        c =>
          String(c.title ?? '').toLowerCase().includes(q) ||
          c.instructor?.toLowerCase().includes(q) ||
          c.tags?.some(t => t.toLowerCase().includes(q))
      );
    }
    if (selectedLevel !== 'All') {
      courses = courses.filter(c => c.level === selectedLevel);
    }
    if (selectedDuration !== 'All') {
      courses = courses.filter(c => {
        const h = c.durationHours || 0;
        switch (selectedDuration) {
          case '< 2 weeks': return h <= 10;
          case '2-4 weeks': return h > 10 && h <= 20;
          case '4-8 weeks': return h > 20 && h <= 40;
          case '8+ weeks': return h > 40;
          default: return true;
        }
      });
    }
    switch (selectedSort) {
      case 'Most Popular':
        courses.sort((a, b) => (b.enrolledCount || 0) - (a.enrolledCount || 0));
        break;
      case 'Highest Rated':
        courses.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'Shortest Duration':
        courses.sort((a, b) => (a.durationHours || 0) - (b.durationHours || 0));
        break;
      case 'Newest':
        courses.reverse();
        break;
      default:
        courses.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
    }
    return courses;
  }, [searchQuery, selectedLevel, selectedSort, selectedDuration]);

  // ── Handlers ──
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1500);
  }, []);

  const handleEnroll = useCallback(
    course => {
      navigation.navigate('CourseDetails', { courseId: course.id, courseData: course });
    },
    [navigation]
  );

  const handleRegisterWebinar = useCallback(
    webinar => {
      if (registeredWebinars.includes(webinar.id)) {
        Alert.alert('Already Registered', 'You are already registered for this webinar.');
        return;
      }
      Alert.alert('Register for Webinar', `Would you like to register for "${webinar.title}"?`, [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Register',
          onPress: () => {
            setRegisteredWebinars(prev => [...prev, webinar.id]);
            Alert.alert('Success!', 'You have been registered. A calendar invite has been sent to your email.');
          },
        },
      ]);
    },
    [registeredWebinars]
  );

  const toggleBookmark = useCallback(courseId => {
    setBookmarkedCourses(prev =>
      prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId]
    );
  }, []);

  const handleShareCourse = useCallback(async course => {
    try {
      await Share.share({
        message: `Check out this course on JOBENG: "${course.title}" by ${course.instructor || 'Expert Instructor'}`,
        title: course.title,
      });
    } catch (err) {
      // Share cancelled or failed
    }
  }, []);

  const markNotificationRead = useCallback(id => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const handleShareCertificate = useCallback(async cert => {
    try {
      await Share.share({
        message: `I earned a certificate in "${cert.title}" from JOBENG Academy! Verify: ${cert.verificationUrl}`,
        title: `Certificate: ${cert.title}`,
      });
    } catch (err) {
      // Share cancelled
    }
  }, []);

  const togglePathExpand = useCallback(pathId => {
    setExpandedPaths(prev =>
      prev.includes(pathId) ? prev.filter(id => id !== pathId) : [...prev, pathId]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedLevel('All');
    setSelectedDuration('All');
    setSelectedSort('Recommended');
    setSearchQuery('');
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedLevel !== 'All') count++;
    if (selectedDuration !== 'All') count++;
    if (selectedSort !== 'Recommended') count++;
    return count;
  }, [selectedLevel, selectedDuration, selectedSort]);

  // ════════════════════════════════════════════════════════════════
  // RENDER: SEARCH BAR
  // ════════════════════════════════════════════════════════════════
  const renderSearchBar = () => (
    <View style={styles.searchSection}>
      <View style={styles.searchBar}>
        <Ionicons name="search" size={18} color={THEME.textMuted} />
        <TextInput
          ref={searchInputRef}
          style={styles.searchInput}
          placeholder="Search courses, instructors, topics..."
          placeholderTextColor={THEME.textMuted}
          value={searchQuery}
          onChangeText={setSearchQuery}
          returnKeyType="search"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={18} color={THEME.textMuted} />
          </TouchableOpacity>
        )}
      </View>
      <TouchableOpacity
        style={[styles.filterButton, activeFilterCount > 0 && styles.filterButtonActive]}
        onPress={() => setShowFilterModal(true)}
      >
        <Ionicons name="options-outline" size={20} color={activeFilterCount > 0 ? THEME.white : THEME.primary} />
        {activeFilterCount > 0 && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>{activeFilterCount}</Text>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: STATS DASHBOARD
  // ════════════════════════════════════════════════════════════════
  const renderStatsDashboard = () => {
    const stats = mockData.userStats;
    return (
      <View style={styles.statsDashboard}>
        <View style={styles.statsRow}>
          <View style={[styles.statCard, { backgroundColor: THEME.primaryBg }]}>
            <MaterialCommunityIcons name="book-check" size={22} color={THEME.primary} />
            <Text style={styles.statValue}>{stats.coursesCompleted}</Text>
            <Text style={styles.statLabel}>Completed</Text>
          </View>
          <View style={[styles.statCard, { backgroundColor: THEME.secondaryLight }]}>
            <MaterialCommunityIcons name="clock-check" size={22} color={THEME.secondary} />
            <Text style={styles.statValue}>{stats.hoursLearned}h</Text>
            <Text style={styles.statLabel}>Learned</Text>
          </View>
          <View style={[styles.statCard, { backgroundColor: THEME.accentLight }]}>
            <MaterialCommunityIcons name="certificate" size={22} color={THEME.accent} />
            <Text style={styles.statValue}>{stats.certificatesEarned}</Text>
            <Text style={styles.statLabel}>Certified</Text>
          </View>
          <View style={[styles.statCard, { backgroundColor: '#f5f3ff' }]}>
            <MaterialCommunityIcons name="fire" size={22} color="#8b5cf6" />
            <Text style={styles.statValue}>{stats.currentStreak}d</Text>
            <Text style={styles.statLabel}>Streak</Text>
          </View>
        </View>

        {/* Weekly Goal Progress */}
        <View style={styles.weeklyGoalCard}>
          <View style={styles.weeklyGoalHeader}>
            <View>
              <Text style={styles.weeklyGoalTitle}>Weekly Goal</Text>
              <Text style={styles.weeklyGoalSubtitle}>
                {stats.weeklyProgress}h / {stats.weeklyGoal}h this week
              </Text>
            </View>
            <View style={styles.weeklyGoalPercentage}>
              <Text style={styles.weeklyGoalPercentText}>
                {Math.round((stats.weeklyProgress / stats.weeklyGoal) * 100)}%
              </Text>
            </View>
          </View>
          <AnimatedProgressBar
            progress={(stats.weeklyProgress / stats.weeklyGoal) * 100}
            color={THEME.secondary}
            height={8}
          />
          {/* Mini Activity Chart */}
          <View style={styles.activityChart}>
            {mockData.weeklyActivity.map((day, index) => (
              <View key={day.day} style={styles.activityBar}>
                <View style={styles.activityBarTrack}>
                  <View
                    style={[
                      styles.activityBarFill,
                      {
                        height: `${Math.max((day.hours / 2.5) * 100, 5)}%`,
                        backgroundColor: day.hours > 0 ? THEME.primary : THEME.border,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.activityDayLabel}>{day.day}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: FEATURED COURSES (Horizontal Carousel)
  // ════════════════════════════════════════════════════════════════
  const renderFeaturedCourse = ({ item }) => {
    const isBookmarked = bookmarkedCourses.includes(item.id);
    return (
      <TouchableOpacity
        style={styles.featuredCourseCard}
        onPress={() => handleEnroll(item)}
        activeOpacity={0.85}
      >
        <View style={styles.courseImageContainer}>
          <Image source={item.image} style={styles.courseImage} resizeMode="cover" />
          <View style={styles.courseImageOverlay}>
            {item.price === 'Free' ? (
              <Chip label="FREE" color="#10b981" bgColor="#ecfdf5" small />
            ) : (
              <Chip label="PREMIUM" color="#f59e0b" bgColor="#fffbeb" small />
            )}
          </View>
          <TouchableOpacity
            style={styles.bookmarkButton}
            onPress={() => toggleBookmark(item.id)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons
              name={isBookmarked ? 'bookmark' : 'bookmark-outline'}
              size={20}
              color={isBookmarked ? THEME.accent : THEME.white}
            />
          </TouchableOpacity>
        </View>
        <View style={styles.courseContent}>
          <Text style={styles.courseTitle} numberOfLines={2}>{item.title}</Text>
          <Text style={styles.courseInstructor}>{item.instructor}</Text>
          <View style={styles.courseMetaRow}>
            <View style={styles.courseMeta}>
              <MaterialCommunityIcons name="clock-outline" size={13} color={THEME.textSecondary} />
              <Text style={styles.courseMetaText}>{item.duration}</Text>
            </View>
            <LevelBadge level={item.level} />
          </View>
          <View style={styles.courseBottomRow}>
            <StarRating rating={item.rating} size={12} showCount count={item.reviewCount} />
            <View style={styles.enrolledBadge}>
              <MaterialCommunityIcons name="account-group-outline" size={12} color={THEME.textMuted} />
              <Text style={styles.enrolledText}>{item.enrolledCount > 999 ? `${(item.enrolledCount / 1000).toFixed(1)}k` : item.enrolledCount}</Text>
            </View>
          </View>
          <View style={styles.tagContainer}>
            {item.tags.slice(0, 3).map((tag, index) => (
              <Chip key={`${item.id}-tag-${index}`} label={tag} small />
            ))}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: CATEGORIES
  // ════════════════════════════════════════════════════════════════
  const renderCategories = () => {
    const visibleCategories = showCategories ? mockData.categories : mockData.categories.slice(0, 6);
    return (
      <View>
        <View style={styles.categoriesGrid}>
          {visibleCategories.map(category => (
            <TouchableOpacity
              key={category.id}
              style={styles.categoryCard}
              onPress={() =>
                navigation.navigate('CategoryCoursesScreen', {
                  categoryId: category.id,
                  categoryName: category.name,
                })
              }
              activeOpacity={0.7}
            >
              <View style={[styles.categoryIconContainer, { backgroundColor: category.bgColor }]}>
                <MaterialCommunityIcons name={category.icon} size={26} color={category.color} />
              </View>
              <Text style={styles.categoryName} numberOfLines={1}>{category.name}</Text>
              <Text style={styles.categoryCount}>{category.courses} courses</Text>
            </TouchableOpacity>
          ))}
        </View>
        {mockData.categories.length > 6 && (
          <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowCategories(!showCategories)}>
            <Text style={styles.showMoreText}>{showCategories ? 'Show Less' : 'Show All Categories'}</Text>
            <Ionicons name={showCategories ? 'chevron-up' : 'chevron-down'} size={16} color={THEME.primary} />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: TRENDING TOPICS
  // ════════════════════════════════════════════════════════════════
  const renderTrendingTopics = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.trendingScroll}>
      {mockData.trendingTopics.map(topic => (
        <TouchableOpacity
          key={topic.id}
          style={styles.trendingChip}
          onPress={() => setSearchQuery(topic.name)}
        >
          <MaterialCommunityIcons name="trending-up" size={14} color={THEME.secondary} />
          <Text style={styles.trendingChipText}>{topic.name}</Text>
          <Text style={styles.trendingGrowth}>{topic.growth}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: RECOMMENDED COURSE CARD
  // ════════════════════════════════════════════════════════════════
  const renderRecommendedCourse = (item) => {
    const isBookmarked = bookmarkedCourses.includes(item.id);
    return (
      <TouchableOpacity
        key={item.id}
        style={styles.recommendedCard}
        onPress={() => handleEnroll(item)}
        activeOpacity={0.85}
      >
        <View style={styles.recommendedContent}>
          <View style={styles.recommendedTitleRow}>
            <Text style={styles.recommendedTitle} numberOfLines={2}>{item.title}</Text>
            <TouchableOpacity
              onPress={() => toggleBookmark(item.id)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name={isBookmarked ? 'bookmark' : 'bookmark-outline'}
                size={18}
                color={isBookmarked ? THEME.accent : THEME.textMuted}
              />
            </TouchableOpacity>
          </View>
          {item.instructor && <Text style={styles.recommendedInstructor}>{item.instructor}</Text>}
          <View style={styles.recommendedMetaRow}>
            <LevelBadge level={item.level} />
            <View style={styles.courseMeta}>
              <MaterialCommunityIcons name="clock-outline" size={13} color={THEME.textSecondary} />
              <Text style={styles.courseMetaText}>{item.duration}</Text>
            </View>
            {item.rating && <StarRating rating={item.rating} size={11} />}
          </View>
          {item.tags && (
            <View style={styles.tagContainer}>
              {item.tags.map((tag, i) => (
                <Chip key={`rec-${item.id}-${i}`} label={tag} small />
              ))}
            </View>
          )}
        </View>
        <View style={styles.matchScoreContainer}>
          <Text style={styles.matchScoreText}>{item.matchScore}%</Text>
          <Text style={styles.matchLabel}>Match</Text>
        </View>
      </TouchableOpacity>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: IN-PROGRESS COURSE CARD
  // ════════════════════════════════════════════════════════════════
  const renderInProgressCourse = (item) => {
    const progressColor =
      item.progress >= 75 ? THEME.secondary :
      item.progress >= 40 ? THEME.accent :
      THEME.primary;

    return (
      <TouchableOpacity
        key={item.id}
        style={styles.inProgressCard}
        onPress={() => navigation.navigate('CoursePlayerScreen', { courseId: item.id })}
        activeOpacity={0.85}
      >
        <View style={styles.inProgressHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.inProgressTitle} numberOfLines={2}>{item.title}</Text>
            <Text style={styles.inProgressInstructor}>{item.instructor}</Text>
          </View>
          <View style={[styles.progressBadge, { backgroundColor: progressColor + '15' }]}>
            <Text style={[styles.progressBadgeText, { color: progressColor }]}>{item.progress}%</Text>
          </View>
        </View>

        <AnimatedProgressBar progress={item.progress} color={progressColor} height={6} />

        <View style={styles.moduleProgress}>
          <Text style={styles.moduleProgressText}>
            Module {item.completedModules}/{item.totalModules}
          </Text>
          <Text style={styles.timeSpentText}>{item.timeSpent} spent</Text>
        </View>

        <View style={styles.nextLessonContainer}>
          <Ionicons name="play-circle-outline" size={16} color={THEME.primary} />
          <Text style={styles.nextLessonText} numberOfLines={1}>{item.nextLesson}</Text>
        </View>

        <View style={styles.inProgressFooter}>
          <View style={styles.inProgressMeta}>
            <MaterialCommunityIcons name="calendar-clock" size={14} color={THEME.textSecondary} />
            <Text style={styles.dueDateText}>Due: {item.dueDate}</Text>
          </View>
          <View style={styles.inProgressMeta}>
            <MaterialCommunityIcons name="timer-sand" size={14} color={THEME.textSecondary} />
            <Text style={styles.dueDateText}>~{item.estimatedTimeLeft} left</Text>
          </View>
          <TouchableOpacity
            style={styles.continueButton}
            onPress={() => navigation.navigate('CoursePlayerScreen', { courseId: item.id })}
          >
            <Text style={styles.continueButtonText}>Continue</Text>
            <Ionicons name="arrow-forward" size={14} color={THEME.white} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: COMPLETED COURSE CARD
  // ════════════════════════════════════════════════════════════════
  const renderCompletedCourse = (item) => (
    <View key={item.id} style={styles.completedCard}>
      <View style={styles.completedHeader}>
        <MaterialCommunityIcons name="check-circle" size={20} color={THEME.secondary} />
        <Text style={styles.completedTitle} numberOfLines={1}>{item.title}</Text>
      </View>
      <View style={styles.completedMeta}>
        <Text style={styles.completedMetaText}>Completed: {item.completedDate}</Text>
        <Text style={styles.completedMetaText}>Score: {item.score}%</Text>
        <Text style={styles.completedMetaText}>{item.hoursLogged}h logged</Text>
      </View>
      <View style={styles.completedFooter}>
        {item.certificate ? (
          <TouchableOpacity
            style={styles.viewCertButton}
            onPress={() => {
              const cert = mockData.certificates.find(c => c.certificateId === item.certificateId);
              if (cert) setShowCertificateModal(cert);
            }}
          >
            <MaterialCommunityIcons name="certificate" size={14} color={THEME.primary} />
            <Text style={styles.viewCertButtonText}>View Certificate</Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.noCertText}>No certificate</Text>
        )}
        <TouchableOpacity onPress={() => handleShareCourse(item)}>
          <Feather name="share-2" size={16} color={THEME.textMuted} />
        </TouchableOpacity>
      </View>
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: WEBINAR CARD
  // ════════════════════════════════════════════════════════════════
  const renderWebinar = (item) => {
    const isRegistered = registeredWebinars.includes(item.id);
    return (
      <View key={item.id} style={styles.webinarCard}>
        <View style={styles.webinarBanner}>
          <View style={styles.webinarDateBadge}>
            <Text style={styles.webinarDateDay}>{item.date.split(' ')[0]}</Text>
            <Text style={styles.webinarDateMonth}>{item.date.split(' ')[1]}</Text>
          </View>
          <View style={styles.webinarBannerContent}>
            <Text style={styles.webinarTitle} numberOfLines={2}>{item.title}</Text>
            <Text style={styles.webinarPresenter}>{item.presenter} • {item.presenterRole}</Text>
          </View>
        </View>

        <View style={styles.webinarDetails}>
          <View style={styles.webinarMetaRow}>
            <View style={styles.webinarMeta}>
              <MaterialCommunityIcons name="clock-outline" size={14} color={THEME.textSecondary} />
              <Text style={styles.webinarMetaText}>{item.time} • {item.duration}</Text>
            </View>
            <View style={styles.webinarMeta}>
              <MaterialCommunityIcons name="account-group-outline" size={14} color={THEME.textSecondary} />
              <Text style={styles.webinarMetaText}>{item.registeredCount} registered</Text>
            </View>
          </View>

          <View style={styles.webinarTopics}>
            {item.topics.map((topic, i) => (
              <Chip key={`wt-${item.id}-${i}`} label={topic} small />
            ))}
            {item.isFree ? (
              <Chip label="Free" color={THEME.secondary} bgColor={THEME.secondaryLight} small />
            ) : (
              <Chip label="Paid" color={THEME.accent} bgColor={THEME.accentLight} small />
            )}
          </View>
        </View>

        <TouchableOpacity
          style={[styles.registerButton, isRegistered && styles.registeredButton]}
          onPress={() => handleRegisterWebinar(item)}
          disabled={isRegistered}
        >
          <MaterialCommunityIcons
            name={isRegistered ? 'check-circle' : 'calendar-plus'}
            size={16}
            color={isRegistered ? THEME.secondary : THEME.white}
          />
          <Text style={[styles.registerButtonText, isRegistered && styles.registeredButtonText]}>
            {isRegistered ? 'Registered' : 'Register Now'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: LEADERBOARD
  // ════════════════════════════════════════════════════════════════
  const renderLeaderboard = () => (
    <View style={styles.leaderboardCard}>
      {mockData.leaderboard.map((entry) => (
        <View
          key={entry.rank}
          style={[
            styles.leaderboardRow,
            entry.isCurrentUser && styles.leaderboardRowHighlight,
          ]}
        >
          <View style={styles.leaderboardRankContainer}>
            {entry.rank <= 3 ? (
              <MaterialCommunityIcons
                name="medal"
                size={22}
                color={entry.rank === 1 ? '#FFD700' : entry.rank === 2 ? '#C0C0C0' : '#CD7F32'}
              />
            ) : (
              <Text style={styles.leaderboardRank}>#{entry.rank}</Text>
            )}
          </View>
          <View style={styles.leaderboardInfo}>
            <View style={styles.leaderboardAvatar}>
              <Text style={styles.leaderboardAvatarText}>{entry.name.charAt(0)}</Text>
            </View>
            <View>
              <Text style={[styles.leaderboardName, entry.isCurrentUser && styles.leaderboardNameHighlight]}>
                {entry.name} {entry.isCurrentUser ? '(You)' : ''}
              </Text>
              <Text style={styles.leaderboardDept}>{entry.department}</Text>
            </View>
          </View>
          <Text style={styles.leaderboardPoints}>{entry.points.toLocaleString()} pts</Text>
        </View>
      ))}
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: SKILL PROGRESS
  // ════════════════════════════════════════════════════════════════
  const renderSkillProgress = () => (
    <View style={styles.skillProgressCard}>
      {mockData.skills.map((skill, index) => (
        <View key={skill.name} style={styles.skillRow}>
          <View style={styles.skillLabelRow}>
            <Text style={styles.skillName}>{skill.name}</Text>
            <Text style={styles.skillLevelText}>{skill.level}/{skill.target}</Text>
          </View>
          <View style={styles.skillBarContainer}>
            <View style={styles.skillBarTrack}>
              <View
                style={[styles.skillBarFill, { width: `${(skill.level / skill.target) * 100}%` }]}
              />
              <View
                style={[styles.skillBarTarget, { left: `${(skill.target / 100) * 100}%` }]}
              />
            </View>
          </View>
        </View>
      ))}
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // TAB: EXPLORE
  // ════════════════════════════════════════════════════════════════
  const renderExploreContent = () => (
    <View>
      {renderSearchBar()}

      {/* Stats Dashboard */}
      {renderStatsDashboard()}

      {/* Trending Topics */}
      <SectionHeader title="Trending Topics" icon="trending-up" />
      {renderTrendingTopics()}

      {/* Featured Courses */}
      <SectionHeader
        title="Featured Courses"
        icon="star-circle"
        actionLabel="See All"
        onAction={() => navigation.navigate('CourseSearchScreen')}
      />
      <FlatList
        data={mockData.featuredCourses}
        renderItem={renderFeaturedCourse}
        keyExtractor={item => item.id}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.featuredCoursesContainer}
        snapToInterval={CARD_WIDTH + 15}
        decelerationRate="fast"
      />

      {/* Browse by Category */}
      <SectionHeader
        title="Browse by Category"
        icon="shape-outline"
      />
      {renderCategories()}

      {/* Recommended for You */}
      <SectionHeader
        title="Recommended for You"
        icon="thumb-up-outline"
        actionLabel="See All"
        onAction={() => navigation.navigate('CourseSearchScreen')}
      />
      {filteredCourses.length > 0 ? (
        filteredCourses.map(course => renderRecommendedCourse(course))
      ) : (
        <EmptyState
          icon="magnify"
          title="No courses found"
          subtitle="Try adjusting your filters or search terms"
          actionLabel="Clear Filters"
          onAction={clearFilters}
        />
      )}

      {/* Upcoming Webinars */}
      <SectionHeader
        title="Upcoming Webinars"
        icon="video-outline"
        actionLabel="View All"
        onAction={() => navigation.navigate('WebinarsScreen')}
      />
      {mockData.upcomingWebinars.map(webinar => renderWebinar(webinar))}

      {/* Leaderboard */}
      <SectionHeader title="Leaderboard" icon="trophy-outline" />
      {renderLeaderboard()}

      {/* Browse All */}
      <TouchableOpacity
        style={styles.browseAllButton}
        onPress={() => navigation.navigate('CourseSearchScreen')}
      >
        <Text style={styles.browseAllButtonText}>Browse All Courses</Text>
        <Ionicons name="arrow-forward" size={16} color={THEME.primary} />
      </TouchableOpacity>
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // TAB: MY LEARNING
  // ════════════════════════════════════════════════════════════════
  const renderMyLearningContent = () => {
    const subTabs = ['In Progress', 'Completed', 'Bookmarked'];
    return (
      <View>
        {/* Sub-Tab Navigation */}
        <View style={styles.subTabContainer}>
          {subTabs.map(tab => (
            <TouchableOpacity
              key={tab}
              style={[styles.subTabButton, myLearningSubTab === tab && styles.subTabButtonActive]}
              onPress={() => setMyLearningSubTab(tab)}
            >
              <Text style={[styles.subTabText, myLearningSubTab === tab && styles.subTabTextActive]}>
                {tab}
              </Text>
              {tab === 'In Progress' && mockData.inProgress.length > 0 && (
                <View style={styles.subTabBadge}>
                  <Text style={styles.subTabBadgeText}>{mockData.inProgress.length}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* Skill Progress */}
        <SectionHeader title="Skill Progress" icon="chart-bar" />
        {renderSkillProgress()}

        {/* Sub-tab content */}
        {myLearningSubTab === 'In Progress' && (
          <>
            <SectionHeader title="Continue Learning" icon="play-circle-outline" />
            {mockData.inProgress.length > 0 ? (
              mockData.inProgress.map(course => renderInProgressCourse(course))
            ) : (
              <EmptyState
                icon="book-open-variant"
                title="No courses in progress"
                subtitle="Enroll in a course to start your learning journey"
                actionLabel="Explore Courses"
                onAction={() => setActiveTab('Explore')}
              />
            )}
          </>
        )}

        {myLearningSubTab === 'Completed' && (
          <>
            <SectionHeader title="Completed Courses" icon="check-circle-outline" />
            {mockData.completedCourses.length > 0 ? (
              mockData.completedCourses.map(course => renderCompletedCourse(course))
            ) : (
              <EmptyState
                icon="trophy-outline"
                title="No completed courses yet"
                subtitle="Complete your first course to see it here"
              />
            )}
          </>
        )}

        {myLearningSubTab === 'Bookmarked' && (
          <>
            <SectionHeader title="Saved Courses" icon="bookmark-outline" />
            {bookmarkedCourses.length > 0 ? (
              [...mockData.featuredCourses, ...mockData.recommendedCourses]
                .filter(c => bookmarkedCourses.includes(c.id))
                .map(course => renderRecommendedCourse(course))
            ) : (
              <EmptyState
                icon="bookmark-outline"
                title="No bookmarked courses"
                subtitle="Tap the bookmark icon on any course to save it here"
                actionLabel="Browse Courses"
                onAction={() => setActiveTab('Explore')}
              />
            )}
          </>
        )}

        {/* Upcoming Webinars in My Learning */}
        <SectionHeader
          title="Your Webinars"
          icon="video-outline"
        />
        {registeredWebinars.length > 0 ? (
          mockData.upcomingWebinars
            .filter(w => registeredWebinars.includes(w.id))
            .map(webinar => renderWebinar(webinar))
        ) : (
          <EmptyState
            icon="video-off-outline"
            title="No registered webinars"
            subtitle="Register for upcoming webinars to see them here"
          />
        )}

        {/* View History */}
        <TouchableOpacity
          style={styles.viewAllButton}
          onPress={() => navigation.navigate('MyLearningHistoryScreen')}
        >
          <Text style={styles.viewAllButtonText}>View Full Learning History</Text>
          <Ionicons name="chevron-forward" size={16} color={THEME.primary} />
        </TouchableOpacity>
      </View>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // TAB: LEARNING PATHS
  // ════════════════════════════════════════════════════════════════
  const renderPathsContent = () => (
    <View>
      <View style={styles.pathsIntro}>
        <MaterialCommunityIcons name="road-variant" size={28} color={THEME.primary} />
        <Text style={styles.pathsIntroTitle}>Learning Paths</Text>
        <Text style={styles.pathsIntroSubtitle}>
          Structured programs to take you from beginner to expert
        </Text>
      </View>

      {mockData.learningPaths.map(path => {
        const isExpanded = expandedPaths.includes(path.id);
        return (
          <View key={path.id} style={styles.pathCard}>
            <TouchableOpacity
              style={styles.pathHeader}
              onPress={() => togglePathExpand(path.id)}
              activeOpacity={0.8}
            >
              <View style={[styles.pathIconContainer, { backgroundColor: path.color + '15' }]}>
                <MaterialCommunityIcons name={path.icon} size={28} color={path.color} />
              </View>
              <View style={styles.pathInfo}>
                <Text style={styles.pathTitle}>{path.title}</Text>
                <Text style={styles.pathDescription}>{path.description}</Text>
                <View style={styles.pathMetaRow}>
                  <Chip label={`${path.coursesCount} courses`} small />
                  <Chip label={`${path.totalHours}h total`} small />
                  <Chip label={path.level} color={path.color} bgColor={path.color + '15'} small />
                </View>
              </View>
              <Ionicons
                name={isExpanded ? 'chevron-up' : 'chevron-down'}
                size={20}
                color={THEME.textMuted}
              />
            </TouchableOpacity>

            {/* Progress Bar */}
            {path.completedCount > 0 && (
              <View style={styles.pathProgress}>
                <AnimatedProgressBar
                  progress={(path.completedCount / path.coursesCount) * 100}
                  color={path.color}
                  height={4}
                  showLabel
                />
              </View>
            )}

            {/* Expanded Course List */}
            {isExpanded && (
              <View style={styles.pathCoursesList}>
                {path.courses.map((courseName, i) => (
                  <View key={`${path.id}-c-${i}`} style={styles.pathCourseItem}>
                    <View style={styles.pathCourseNumber}>
                      {i < path.completedCount ? (
                        <MaterialCommunityIcons name="check-circle" size={20} color={THEME.secondary} />
                      ) : (
                        <Text style={styles.pathCourseNumberText}>{i + 1}</Text>
                      )}
                    </View>
                    <Text
                      style={[
                        styles.pathCourseName,
                        i < path.completedCount && styles.pathCourseNameCompleted,
                      ]}
                    >
                      {courseName}
                    </Text>
                    {i === path.completedCount && (
                      <Chip label="Next" color={path.color} bgColor={path.color + '15'} small />
                    )}
                  </View>
                ))}
                <TouchableOpacity style={[styles.enrollPathButton, { backgroundColor: path.color }]}>
                  <Text style={styles.enrollPathButtonText}>
                    {path.completedCount > 0 ? 'Continue Path' : 'Start Path'}
                  </Text>
                  <Ionicons name="arrow-forward" size={16} color={THEME.white} />
                </TouchableOpacity>
              </View>
            )}

            {/* Enrolled Count */}
            <View style={styles.pathEnrolled}>
              <MaterialCommunityIcons name="account-group-outline" size={14} color={THEME.textMuted} />
              <Text style={styles.pathEnrolledText}>{path.enrolledCount} enrolled</Text>
            </View>
          </View>
        );
      })}
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // TAB: CERTIFICATES
  // ════════════════════════════════════════════════════════════════
  const renderCertificatesContent = () => (
    <View>
      <View style={styles.certStatsRow}>
        <View style={[styles.certStatCard, { backgroundColor: THEME.primaryBg }]}>
          <Text style={[styles.certStatValue, { color: THEME.primary }]}>{mockData.certificates.filter(c => c.status === 'Active').length}</Text>
          <Text style={styles.certStatLabel}>Active</Text>
        </View>
        <View style={[styles.certStatCard, { backgroundColor: THEME.dangerLight }]}>
          <Text style={[styles.certStatValue, { color: THEME.danger }]}>{mockData.certificates.filter(c => c.status === 'Expired').length}</Text>
          <Text style={styles.certStatLabel}>Expired</Text>
        </View>
        <View style={[styles.certStatCard, { backgroundColor: THEME.accentLight }]}>
          <Text style={[styles.certStatValue, { color: THEME.accent }]}>{mockData.certificates.filter(c => c.expiryDate).length}</Text>
          <Text style={styles.certStatLabel}>Expiring</Text>
        </View>
      </View>

      {mockData.certificates.map(cert => (
        <View
          key={cert.id}
          style={[styles.certificateCard, cert.status === 'Expired' && styles.certificateCardExpired]}
        >
          <View style={styles.certificateHeader}>
            <View style={[styles.certificateIconContainer, { backgroundColor: cert.status === 'Active' ? THEME.secondaryLight : THEME.dangerLight }]}>
              <MaterialCommunityIcons
                name="certificate"
                size={24}
                color={cert.status === 'Active' ? THEME.secondary : THEME.danger}
              />
            </View>
            <View style={styles.certificateInfo}>
              <Text style={styles.certificateTitle}>{cert.title}</Text>
              <Text style={styles.certificateIssuer}>{cert.issuer} • {cert.certificateId}</Text>
            </View>
            <Chip
              label={cert.status}
              color={cert.status === 'Active' ? THEME.secondary : THEME.danger}
              bgColor={cert.status === 'Active' ? THEME.secondaryLight : THEME.dangerLight}
              small
            />
          </View>

          <View style={styles.certificateMeta}>
            <View style={styles.certificateMetaItem}>
              <MaterialCommunityIcons name="calendar-check" size={14} color={THEME.textSecondary} />
              <Text style={styles.certificateMetaText}>Issued: {cert.issuedDate}</Text>
            </View>
            {cert.expiryDate && (
              <View style={styles.certificateMetaItem}>
                <MaterialCommunityIcons name="calendar-alert" size={14} color={cert.status === 'Expired' ? THEME.danger : THEME.textSecondary} />
                <Text style={[styles.certificateMetaText, cert.status === 'Expired' && { color: THEME.danger }]}>
                  Expires: {cert.expiryDate}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.certificateActions}>
            <TouchableOpacity
              style={styles.certActionButton}
              onPress={() => setShowCertificateModal(cert)}
            >
              <Feather name="eye" size={14} color={THEME.primary} />
              <Text style={styles.certActionText}>View</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.certActionButton}
              onPress={() => handleShareCertificate(cert)}
            >
              <Feather name="share-2" size={14} color={THEME.primary} />
              <Text style={styles.certActionText}>Share</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.certActionButton}
              onPress={() => Alert.alert('Download', 'Certificate PDF will be downloaded.')}
            >
              <Feather name="download" size={14} color={THEME.primary} />
              <Text style={styles.certActionText}>Download</Text>
            </TouchableOpacity>
            {cert.status === 'Expired' && (
              <TouchableOpacity
                style={[styles.certActionButton, styles.certRenewButton]}
                onPress={() => Alert.alert('Renew', 'You will be redirected to the renewal course.')}
              >
                <MaterialCommunityIcons name="refresh" size={14} color={THEME.white} />
                <Text style={[styles.certActionText, { color: THEME.white }]}>Renew</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      ))}
    </View>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: FILTER MODAL
  // ════════════════════════════════════════════════════════════════
  const renderFilterModal = () => (
    <Modal
      visible={showFilterModal}
      animationType="slide"
      transparent
      onRequestClose={() => setShowFilterModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.filterModalContent}>
          <View style={styles.filterModalHeader}>
            <Text style={styles.filterModalTitle}>Filters & Sort</Text>
            <TouchableOpacity onPress={() => setShowFilterModal(false)}>
              <Ionicons name="close" size={24} color={THEME.text} />
            </TouchableOpacity>
          </View>

          {/* Level Filter */}
          <Text style={styles.filterLabel}>Level</Text>
          <View style={styles.filterChips}>
            {['All', ...LEVELS].map(level => (
              <TouchableOpacity
                key={level}
                style={[styles.filterChip, selectedLevel === level && styles.filterChipActive]}
                onPress={() => setSelectedLevel(level)}
              >
                <Text style={[styles.filterChipText, selectedLevel === level && styles.filterChipTextActive]}>
                  {level}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Duration Filter */}
          <Text style={styles.filterLabel}>Duration</Text>
          <View style={styles.filterChips}>
            {FILTER_DURATIONS.map(dur => (
              <TouchableOpacity
                key={dur}
                style={[styles.filterChip, selectedDuration === dur && styles.filterChipActive]}
                onPress={() => setSelectedDuration(dur)}
              >
                <Text style={[styles.filterChipText, selectedDuration === dur && styles.filterChipTextActive]}>
                  {dur}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Sort */}
          <Text style={styles.filterLabel}>Sort By</Text>
          <View style={styles.filterChips}>
            {SORT_OPTIONS.map(opt => (
              <TouchableOpacity
                key={opt}
                style={[styles.filterChip, selectedSort === opt && styles.filterChipActive]}
                onPress={() => setSelectedSort(opt)}
              >
                <Text style={[styles.filterChipText, selectedSort === opt && styles.filterChipTextActive]}>
                  {opt}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Actions */}
          <View style={styles.filterActions}>
            <TouchableOpacity style={styles.clearFiltersButton} onPress={clearFilters}>
              <Text style={styles.clearFiltersText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.applyFiltersButton}
              onPress={() => setShowFilterModal(false)}
            >
              <Text style={styles.applyFiltersText}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER: NOTIFICATION MODAL
  // ════════════════════════════════════════════════════════════════
  const renderNotificationModal = () => {
    const notifIcons = {
      deadline: 'calendar-alert',
      achievement: 'trophy',
      webinar: 'video',
      new_course: 'book-plus',
      certificate: 'certificate',
    };
    const notifColors = {
      deadline: THEME.accent,
      achievement: '#8b5cf6',
      webinar: THEME.primary,
      new_course: THEME.secondary,
      certificate: THEME.danger,
    };

    return (
      <Modal
        visible={showNotifications}
        animationType="slide"
        transparent
        onRequestClose={() => setShowNotifications(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.notificationModalContent}>
            <View style={styles.filterModalHeader}>
              <Text style={styles.filterModalTitle}>Notifications</Text>
              <View style={styles.notifHeaderActions}>
                {unreadNotifications > 0 && (
                  <TouchableOpacity onPress={markAllNotificationsRead} style={{ marginRight: 12 }}>
                    <Text style={styles.markAllReadText}>Mark all read</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => setShowNotifications(false)}>
                  <Ionicons name="close" size={24} color={THEME.text} />
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              {notifications.map(notif => (
                <TouchableOpacity
                  key={notif.id}
                  style={[styles.notifItem, !notif.read && styles.notifItemUnread]}
                  onPress={() => markNotificationRead(notif.id)}
                >
                  <View style={[styles.notifIconContainer, { backgroundColor: (notifColors[notif.type] || THEME.primary) + '15' }]}>
                    <MaterialCommunityIcons
                      name={notifIcons[notif.type] || 'bell'}
                      size={20}
                      color={notifColors[notif.type] || THEME.primary}
                    />
                  </View>
                  <View style={styles.notifContent}>
                    <Text style={[styles.notifMessage, !notif.read && styles.notifMessageUnread]}>
                      {notif.message}
                    </Text>
                    <Text style={styles.notifTimestamp}>{notif.timestamp}</Text>
                  </View>
                  {!notif.read && <View style={styles.notifDot} />}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: CERTIFICATE DETAIL MODAL
  // ════════════════════════════════════════════════════════════════
  const renderCertificateModal = () => {
    if (!showCertificateModal) return null;
    const cert = showCertificateModal;
    return (
      <Modal
        visible={!!showCertificateModal}
        animationType="fade"
        transparent
        onRequestClose={() => setShowCertificateModal(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.certModalContent}>
            <TouchableOpacity
              style={styles.certModalClose}
              onPress={() => setShowCertificateModal(null)}
            >
              <Ionicons name="close" size={24} color={THEME.text} />
            </TouchableOpacity>

            <View style={styles.certModalBody}>
              <View style={styles.certModalIcon}>
                <MaterialCommunityIcons name="certificate" size={48} color={THEME.primary} />
              </View>
              <Text style={styles.certModalTitle}>Certificate of Completion</Text>
              <Text style={styles.certModalCourse}>{cert.title}</Text>
              <Text style={styles.certModalIssuer}>Issued by {cert.issuer}</Text>
              <Text style={styles.certModalDate}>Date: {cert.issuedDate}</Text>
              <Text style={styles.certModalId}>ID: {cert.certificateId}</Text>
              {cert.expiryDate && (
                <Text style={[styles.certModalExpiry, cert.status === 'Expired' && { color: THEME.danger }]}>
                  {cert.status === 'Expired' ? 'Expired' : 'Expires'}: {cert.expiryDate}
                </Text>
              )}

              <View style={styles.certModalActions}>
                <TouchableOpacity
                  style={[styles.certModalButton, { backgroundColor: THEME.primary }]}
                  onPress={() => handleShareCertificate(cert)}
                >
                  <Feather name="share-2" size={16} color={THEME.white} />
                  <Text style={styles.certModalButtonText}>Share</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.certModalButton, { backgroundColor: THEME.secondary }]}
                  onPress={() => Alert.alert('Download', 'PDF downloaded successfully.')}
                >
                  <Feather name="download" size={16} color={THEME.white} />
                  <Text style={styles.certModalButtonText}>Download PDF</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // TAB CONTENT ROUTER
  // ════════════════════════════════════════════════════════════════
  const getTabContent = () => {
    switch (activeTab) {
      case 'Explore': return renderExploreContent();
      case 'My Learning': return renderMyLearningContent();
      case 'Paths': return renderPathsContent();
      case 'Certificates': return renderCertificatesContent();
      default: return renderExploreContent();
    }
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case 'Explore': return 'compass-outline';
      case 'My Learning': return 'book-open-outline';
      case 'Paths': return 'road-variant';
      case 'Certificates': return 'certificate';
      default: return 'circle-outline';
    }
  };

  // ════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={THEME.primary} />

      {/* ── Header ── */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerBack}>
          <Ionicons name="chevron-back" size={24} color={THEME.white} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Development & Training</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerIconButton}
            onPress={() => setShowNotifications(true)}
          >
            <Ionicons name="notifications-outline" size={22} color={THEME.white} />
            <NotificationBadge count={unreadNotifications} />
          </TouchableOpacity>
          <JobengLogo />
        </View>
      </View>

      {/* ── Tab Navigation ── */}
      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabScroll}>
          {TABS.map(tab => (
            <TouchableOpacity
              key={tab}
              style={[styles.tabButton, activeTab === tab && styles.activeTabButton]}
              onPress={() => setActiveTab(tab)}
            >
              <MaterialCommunityIcons
                name={getTabIcon(tab)}
                size={18}
                color={activeTab === tab ? THEME.primary : THEME.textMuted}
                style={{ marginRight: 5 }}
              />
              <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* ── Main Scrollable Content ── */}
      <ScrollView
        ref={scrollRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[THEME.primary]}
            tintColor={THEME.primary}
          />
        }
      >
        {getTabContent()}
      </ScrollView>

      {/* ── Modals ── */}
      {renderFilterModal()}
      {renderNotificationModal()}
      {renderCertificateModal()}
    </SafeAreaView>
  );
};

// ════════════════════════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════════════════════════
const styles = StyleSheet.create({
  // ── Layout ──
  safeArea: {
    flex: 1,
    backgroundColor: THEME.bg,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 40,
  },

  // ── Header ──
  header: {
    backgroundColor: THEME.primary,
    paddingVertical: 14,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerBack: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: THEME.white,
    flex: 1,
    textAlign: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerIconButton: {
    position: 'relative',
    padding: 4,
  },

  // ── Notification Badge ──
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -4,
    backgroundColor: THEME.danger,
    borderRadius: 9,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    borderWidth: 1.5,
    borderColor: THEME.primary,
  },
  notificationBadgeText: {
    color: THEME.white,
    fontSize: 10,
    fontWeight: '700',
  },

  // ── Tabs ──
  tabContainer: {
    backgroundColor: THEME.white,
    borderBottomWidth: 1,
    borderBottomColor: THEME.border,
  },
  tabScroll: {
    paddingHorizontal: 16,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 13,
    paddingHorizontal: 12,
    marginRight: 4,
    borderBottomWidth: 2.5,
    borderBottomColor: 'transparent',
  },
  activeTabButton: {
    borderBottomColor: THEME.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: THEME.textMuted,
  },
  activeTabText: {
    color: THEME.primary,
    fontWeight: '700',
  },

  // ── Search ──
  searchSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
    gap: 10,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: THEME.white,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 12 : 8,
    borderWidth: 1,
    borderColor: THEME.border,
    ...SHADOW,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: THEME.text,
    marginLeft: 8,
    padding: 0,
  },
  filterButton: {
    backgroundColor: THEME.white,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: THEME.border,
    ...SHADOW,
  },
  filterButtonActive: {
    backgroundColor: THEME.primary,
    borderColor: THEME.primary,
  },
  filterBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: THEME.danger,
    borderRadius: 8,
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterBadgeText: {
    color: THEME.white,
    fontSize: 9,
    fontWeight: '700',
  },

  // ── Stats Dashboard ──
  statsDashboard: {
    marginTop: 12,
    marginBottom: 8,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 12,
  },
  statCard: {
    flex: 1,
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    ...SHADOW,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '800',
    color: THEME.text,
    marginTop: 4,
  },
  statLabel: {
    fontSize: 10,
    color: THEME.textSecondary,
    marginTop: 2,
    fontWeight: '500',
  },
  weeklyGoalCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 16,
    ...SHADOW,
  },
  weeklyGoalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  weeklyGoalTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: THEME.text,
  },
  weeklyGoalSubtitle: {
    fontSize: 12,
    color: THEME.textSecondary,
    marginTop: 2,
  },
  weeklyGoalPercentage: {
    backgroundColor: THEME.secondaryLight,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  weeklyGoalPercentText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.secondary,
  },
  activityChart: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    height: 60,
  },
  activityBar: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 3,
  },
  activityBarTrack: {
    flex: 1,
    width: '100%',
    backgroundColor: THEME.borderLight,
    borderRadius: 4,
    overflow: 'hidden',
    justifyContent: 'flex-end',
  },
  activityBarFill: {
    width: '100%',
    borderRadius: 4,
  },
  activityDayLabel: {
    fontSize: 10,
    color: THEME.textMuted,
    marginTop: 4,
    fontWeight: '500',
  },

  // ── Section Header ──
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 12,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: THEME.text,
  },
  sectionAction: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionActionText: {
    fontSize: 13,
    fontWeight: '600',
    color: THEME.primary,
    marginRight: 2,
  },

  // ── Featured Courses ──
  featuredCoursesContainer: {
    paddingRight: 16,
    paddingBottom: 4,
  },
  featuredCourseCard: {
    width: CARD_WIDTH,
    backgroundColor: THEME.white,
    borderRadius: 14,
    marginRight: 15,
    ...SHADOW_HEAVY,
    overflow: 'hidden',
  },
  courseImageContainer: {
    position: 'relative',
  },
  courseImage: {
    width: '100%',
    height: 130,
    backgroundColor: THEME.borderLight,
  },
  courseImageOverlay: {
    position: 'absolute',
    top: 10,
    left: 10,
  },
  bookmarkButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(0,0,0,0.35)',
    borderRadius: 20,
    padding: 6,
  },
  courseContent: {
    padding: 14,
  },
  courseTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: THEME.text,
    marginBottom: 4,
    lineHeight: 20,
  },
  courseInstructor: {
    fontSize: 12,
    color: THEME.textSecondary,
    marginBottom: 8,
  },
  courseMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  courseMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  courseMetaText: {
    fontSize: 11,
    color: THEME.textSecondary,
    marginLeft: 3,
  },
  courseBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  enrolledBadge: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  enrolledText: {
    fontSize: 11,
    color: THEME.textMuted,
    marginLeft: 3,
  },
  tagContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },

  // ── Chips ──
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  chipSmall: {
    paddingHorizontal: 7,
    paddingVertical: 3,
  },
  chipText: {
    fontSize: 11,
    fontWeight: '600',
  },
  chipTextSmall: {
    fontSize: 10,
  },

  // ── Star Rating ──
  starContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ratingText: {
    fontWeight: '700',
    color: THEME.text,
    marginLeft: 3,
  },
  ratingCount: {
    color: THEME.textMuted,
    marginLeft: 2,
  },

  // ── Categories ──
  categoriesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 10,
  },
  categoryCard: {
    width: '31%',
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 14,
    alignItems: 'center',
    ...SHADOW,
  },
  categoryIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  categoryName: {
    fontSize: 12,
    fontWeight: '600',
    color: THEME.text,
    textAlign: 'center',
    marginBottom: 2,
  },
  categoryCount: {
    fontSize: 10,
    color: THEME.textMuted,
    textAlign: 'center',
  },
  showMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
    paddingVertical: 8,
  },
  showMoreText: {
    fontSize: 13,
    fontWeight: '600',
    color: THEME.primary,
    marginRight: 4,
  },

  // ── Trending ──
  trendingScroll: {
    paddingBottom: 4,
    gap: 8,
  },
  trendingChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: THEME.white,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: THEME.border,
    gap: 5,
  },
  trendingChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: THEME.text,
  },
  trendingGrowth: {
    fontSize: 10,
    fontWeight: '700',
    color: THEME.secondary,
  },

  // ── Recommended ──
  recommendedCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    flexDirection: 'row',
    ...SHADOW,
  },
  recommendedContent: {
    flex: 1,
    marginRight: 10,
  },
  recommendedTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  recommendedTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: THEME.text,
    marginBottom: 4,
    flex: 1,
    marginRight: 8,
  },
  recommendedInstructor: {
    fontSize: 12,
    color: THEME.textSecondary,
    marginBottom: 6,
  },
  recommendedMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  matchScoreContainer: {
    backgroundColor: THEME.primaryBg,
    borderRadius: 12,
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
    alignSelf: 'center',
  },
  matchScoreText: {
    fontSize: 18,
    fontWeight: '800',
    color: THEME.primary,
  },
  matchLabel: {
    fontSize: 10,
    color: THEME.textSecondary,
    fontWeight: '500',
  },

  // ── In Progress ──
  inProgressCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    ...SHADOW,
  },
  inProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  inProgressTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: THEME.text,
    marginBottom: 2,
  },
  inProgressInstructor: {
    fontSize: 12,
    color: THEME.textSecondary,
  },
  progressBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 8,
  },
  progressBadgeText: {
    fontSize: 13,
    fontWeight: '700',
  },
  progressBarContainer: {
    backgroundColor: THEME.borderLight,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressBar: {
    borderRadius: 4,
  },
  progressLabelText: {
    fontSize: 10,
    color: THEME.textSecondary,
    marginTop: 3,
    textAlign: 'right',
  },
  moduleProgress: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  moduleProgressText: {
    fontSize: 11,
    color: THEME.textSecondary,
    fontWeight: '500',
  },
  timeSpentText: {
    fontSize: 11,
    color: THEME.textSecondary,
  },
  nextLessonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: THEME.primaryBg,
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
    gap: 6,
  },
  nextLessonText: {
    fontSize: 13,
    color: THEME.primary,
    fontWeight: '500',
    flex: 1,
  },
  inProgressFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  inProgressMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  dueDateText: {
    fontSize: 11,
    color: THEME.textSecondary,
  },
  continueButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: THEME.primary,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 4,
  },
  continueButtonText: {
    fontSize: 12,
    fontWeight: '700',
    color: THEME.white,
  },

  // ── Completed ──
  completedCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    ...SHADOW,
  },
  completedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  completedTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: THEME.text,
    flex: 1,
  },
  completedMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 10,
  },
  completedMetaText: {
    fontSize: 11,
    color: THEME.textSecondary,
  },
  completedFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewCertButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  viewCertButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: THEME.primary,
  },
  noCertText: {
    fontSize: 11,
    color: THEME.textMuted,
    fontStyle: 'italic',
  },

  // ── Webinar ──
  webinarCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
    ...SHADOW,
  },
  webinarBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    gap: 12,
  },
  webinarDateBadge: {
    backgroundColor: THEME.primaryBg,
    borderRadius: 10,
    padding: 10,
    alignItems: 'center',
    minWidth: 50,
  },
  webinarDateDay: {
    fontSize: 18,
    fontWeight: '800',
    color: THEME.primary,
  },
  webinarDateMonth: {
    fontSize: 11,
    color: THEME.primary,
    fontWeight: '500',
  },
  webinarBannerContent: {
    flex: 1,
  },
  webinarTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: THEME.text,
    marginBottom: 4,
  },
  webinarPresenter: {
    fontSize: 12,
    color: THEME.textSecondary,
  },
  webinarDetails: {
    marginBottom: 12,
  },
  webinarMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  webinarMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  webinarMetaText: {
    fontSize: 11,
    color: THEME.textSecondary,
  },
  webinarTopics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  registerButton: {
    backgroundColor: THEME.primary,
    paddingVertical: 11,
    borderRadius: 10,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  registeredButton: {
    backgroundColor: THEME.secondaryLight,
    borderWidth: 1,
    borderColor: THEME.secondary,
  },
  registerButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.white,
  },
  registeredButtonText: {
    color: THEME.secondary,
  },

  // ── Leaderboard ──
  leaderboardCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    overflow: 'hidden',
    ...SHADOW,
  },
  leaderboardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: THEME.borderLight,
  },
  leaderboardRowHighlight: {
    backgroundColor: THEME.primaryBg,
  },
  leaderboardRankContainer: {
    width: 30,
    alignItems: 'center',
  },
  leaderboardRank: {
    fontSize: 14,
    fontWeight: '600',
    color: THEME.textSecondary,
  },
  leaderboardInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginLeft: 8,
  },
  leaderboardAvatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: THEME.primaryBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leaderboardAvatarText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.primary,
  },
  leaderboardName: {
    fontSize: 13,
    fontWeight: '600',
    color: THEME.text,
  },
  leaderboardNameHighlight: {
    color: THEME.primary,
    fontWeight: '700',
  },
  leaderboardDept: {
    fontSize: 10,
    color: THEME.textMuted,
  },
  leaderboardPoints: {
    fontSize: 13,
    fontWeight: '700',
    color: THEME.text,
  },

  // ── Skill Progress ──
  skillProgressCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 16,
    ...SHADOW,
  },
  skillRow: {
    marginBottom: 14,
  },
  skillLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  skillName: {
    fontSize: 13,
    fontWeight: '600',
    color: THEME.text,
  },
  skillLevelText: {
    fontSize: 11,
    color: THEME.textSecondary,
  },
  skillBarContainer: {
    height: 6,
  },
  skillBarTrack: {
    height: 6,
    backgroundColor: THEME.borderLight,
    borderRadius: 3,
    overflow: 'visible',
    position: 'relative',
  },
  skillBarFill: {
    height: 6,
    backgroundColor: THEME.primary,
    borderRadius: 3,
  },
  skillBarTarget: {
    position: 'absolute',
    top: -2,
    width: 2,
    height: 10,
    backgroundColor: THEME.danger,
    borderRadius: 1,
  },

  // ── Sub-Tabs ──
  subTabContainer: {
    flexDirection: 'row',
    marginTop: 12,
    backgroundColor: THEME.borderLight,
    borderRadius: 12,
    padding: 3,
  },
  subTabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 10,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 4,
  },
  subTabButtonActive: {
    backgroundColor: THEME.white,
    ...SHADOW,
  },
  subTabText: {
    fontSize: 13,
    fontWeight: '500',
    color: THEME.textSecondary,
  },
  subTabTextActive: {
    fontWeight: '700',
    color: THEME.primary,
  },
  subTabBadge: {
    backgroundColor: THEME.primary,
    borderRadius: 8,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  subTabBadgeText: {
    color: THEME.white,
    fontSize: 10,
    fontWeight: '700',
  },

  // ── Learning Paths ──
  pathsIntro: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  pathsIntroTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: THEME.text,
    marginTop: 8,
  },
  pathsIntroSubtitle: {
    fontSize: 13,
    color: THEME.textSecondary,
    textAlign: 'center',
    marginTop: 4,
  },
  pathCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    marginBottom: 14,
    overflow: 'hidden',
    ...SHADOW,
  },
  pathHeader: {
    flexDirection: 'row',
    padding: 16,
    alignItems: 'flex-start',
    gap: 12,
  },
  pathIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pathInfo: {
    flex: 1,
  },
  pathTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: THEME.text,
    marginBottom: 3,
  },
  pathDescription: {
    fontSize: 12,
    color: THEME.textSecondary,
    marginBottom: 8,
  },
  pathMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  pathProgress: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  pathCoursesList: {
    borderTopWidth: 1,
    borderTopColor: THEME.borderLight,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  pathCourseItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: THEME.borderLight,
    gap: 10,
  },
  pathCourseNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: THEME.borderLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pathCourseNumberText: {
    fontSize: 12,
    fontWeight: '700',
    color: THEME.textSecondary,
  },
  pathCourseName: {
    flex: 1,
    fontSize: 13,
    color: THEME.text,
    fontWeight: '500',
  },
  pathCourseNameCompleted: {
    color: THEME.textMuted,
    textDecorationLine: 'line-through',
  },
  enrollPathButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    marginTop: 12,
    gap: 6,
  },
  enrollPathButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.white,
  },
  pathEnrolled: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 4,
  },
  pathEnrolledText: {
    fontSize: 11,
    color: THEME.textMuted,
  },

  // ── Certificates Tab ──
  certStatsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
    marginBottom: 16,
  },
  certStatCard: {
    flex: 1,
    borderRadius: 14,
    padding: 16,
    alignItems: 'center',
    ...SHADOW,
  },
  certStatValue: {
    fontSize: 24,
    fontWeight: '800',
  },
  certStatLabel: {
    fontSize: 11,
    color: THEME.textSecondary,
    marginTop: 2,
  },
  certificateCard: {
    backgroundColor: THEME.white,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    ...SHADOW,
  },
  certificateCardExpired: {
    opacity: 0.75,
  },
  certificateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 10,
  },
  certificateIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  certificateInfo: {
    flex: 1,
  },
  certificateTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.text,
  },
  certificateIssuer: {
    fontSize: 11,
    color: THEME.textSecondary,
    marginTop: 2,
  },
  certificateMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginBottom: 12,
  },
  certificateMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  certificateMetaText: {
    fontSize: 11,
    color: THEME.textSecondary,
  },
  certificateActions: {
    flexDirection: 'row',
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: THEME.borderLight,
    paddingTop: 10,
  },
  certActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: THEME.primaryBg,
  },
  certRenewButton: {
    backgroundColor: THEME.danger,
  },
  certActionText: {
    fontSize: 12,
    fontWeight: '600',
    color: THEME.primary,
  },

  // ── Buttons ──
  browseAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    marginTop: 10,
    marginBottom: 20,
    backgroundColor: THEME.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: THEME.border,
    gap: 6,
  },
  browseAllButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.primary,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    marginTop: 10,
    marginBottom: 20,
    gap: 4,
  },
  viewAllButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: THEME.primary,
  },

  // ── Empty State ──
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
    backgroundColor: THEME.borderLight,
    borderRadius: 14,
    marginBottom: 16,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '700',
    color: THEME.textSecondary,
    marginTop: 12,
    marginBottom: 4,
  },
  emptyStateSubtext: {
    fontSize: 13,
    color: THEME.textMuted,
    textAlign: 'center',
    lineHeight: 18,
  },
  emptyStateButton: {
    marginTop: 14,
    backgroundColor: THEME.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 10,
  },
  emptyStateButtonText: {
    color: THEME.white,
    fontSize: 13,
    fontWeight: '700',
  },

  // ── Modal ──
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  filterModalContent: {
    backgroundColor: THEME.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    maxHeight: SCREEN_HEIGHT * 0.75,
  },
  filterModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  filterModalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: THEME.text,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.text,
    marginTop: 14,
    marginBottom: 8,
  },
  filterChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: THEME.borderLight,
    borderWidth: 1,
    borderColor: THEME.border,
  },
  filterChipActive: {
    backgroundColor: THEME.primary,
    borderColor: THEME.primary,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: '500',
    color: THEME.text,
  },
  filterChipTextActive: {
    color: THEME.white,
    fontWeight: '700',
  },
  filterActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
    gap: 12,
  },
  clearFiltersButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: THEME.border,
  },
  clearFiltersText: {
    fontSize: 14,
    fontWeight: '600',
    color: THEME.textSecondary,
  },
  applyFiltersButton: {
    flex: 2,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    backgroundColor: THEME.primary,
  },
  applyFiltersText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.white,
  },

  // ── Notification Modal ──
  notificationModalContent: {
    backgroundColor: THEME.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    maxHeight: SCREEN_HEIGHT * 0.7,
  },
  notifHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  markAllReadText: {
    fontSize: 13,
    fontWeight: '600',
    color: THEME.primary,
  },
  notifItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: THEME.borderLight,
    gap: 10,
  },
  notifItemUnread: {
    backgroundColor: THEME.primaryBg,
    marginHorizontal: -8,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  notifIconContainer: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notifContent: {
    flex: 1,
  },
  notifMessage: {
    fontSize: 13,
    color: THEME.text,
    lineHeight: 18,
  },
  notifMessageUnread: {
    fontWeight: '600',
  },
  notifTimestamp: {
    fontSize: 11,
    color: THEME.textMuted,
    marginTop: 2,
  },
  notifDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: THEME.primary,
  },

  // ── Certificate Modal ──
  certModalContent: {
    backgroundColor: THEME.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
  },
  certModalClose: {
    alignSelf: 'flex-end',
    padding: 4,
  },
  certModalBody: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  certModalIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: THEME.primaryBg,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  certModalTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: THEME.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 8,
  },
  certModalCourse: {
    fontSize: 20,
    fontWeight: '800',
    color: THEME.text,
    textAlign: 'center',
    marginBottom: 6,
  },
  certModalIssuer: {
    fontSize: 13,
    color: THEME.textSecondary,
    marginBottom: 4,
  },
  certModalDate: {
    fontSize: 13,
    color: THEME.textSecondary,
    marginBottom: 2,
  },
  certModalId: {
    fontSize: 12,
    color: THEME.textMuted,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    marginBottom: 4,
  },
  certModalExpiry: {
    fontSize: 12,
    color: THEME.textSecondary,
    marginBottom: 20,
  },
  certModalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  certModalButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
    gap: 6,
  },
  certModalButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: THEME.white,
  },
});

export default DevelopmentTrainingScreen;
